import {
    $generateHtmlFromNodes,
    $isRootTextContentEmpty,
    LexicalComposer,
    editorTheme,
    LinkNode,
    HeadingNode,
    ParagraphNode,
    ListNode,
    ListItemNode,
    HistoryPlugin,
    ToolbarPlugin,
    OnChangePlugin,
    ListPlugin,
    LinkPlugin,
    RichTextPlugin,
    LexicalErrorBoundary,
    RichContentEditable,
    PrefillPlugin,
    RTEInitValue,
    initialPrefillTag,
} from '@talentmesh/rte';
import { FieldError } from '@talentmesh/ui/components/field';
import { useController } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useRecruiterSignature } from '../../api/useRecruiterSignature';
import EmailTemplatePlaceholders from '../../Components/EmailTemplatePlaceholders';
import { useProfileInfoFormContext } from './useProfileInfoForm';
import PlaceholderPlugins, {
    Placeholder,
} from '@/Pages/Candidates/AiSearch/components/ShortlistedList/components/RTE/PlaceholderPlugins';
import UIStrings from '@/Utils/UIStrings';

const signaturePlaceholders: Placeholder[] = [
    { label: UIStrings.FirstName, value: EmailTemplatePlaceholders.FirstName },
    { label: UIStrings.LastName, value: EmailTemplatePlaceholders.LastName },
    { label: UIStrings.CompanyName, value: EmailTemplatePlaceholders.CompanyName },
];

const ProfileSignatureRTE = () => {
    const form = useProfileInfoFormContext();
    const { data: recruiterSignature } = useRecruiterSignature();
    const { fieldState: signatureFieldState } = useController({ control: form.control, name: 'signature' });
    const signatureFieldErrorMessage = signatureFieldState.error?.message;

    const handleEditorOnChange = useDebounceCallback<Parameters<typeof OnChangePlugin>['0']['onChange']>(
        (editorState, editor, tags) => {
            editorState.read(() => {
                let signatureToSet = $generateHtmlFromNodes(editor);
                const isPrefill = tags.has(initialPrefillTag);

                if ($isRootTextContentEmpty(editor.isComposing(), true)) {
                    signatureToSet = '';
                }

                form.setValue('signature', signatureToSet, {
                    shouldValidate: true,
                    shouldDirty: !isPrefill,
                    shouldTouch: !isPrefill,
                });
            });
        },
        10,
    );

    return (
        <>
            <LexicalComposer
                initialConfig={{
                    namespace: 'Recruiter signature editor',
                    onError: (error) => form.setError('signature', { message: error.message }),
                    theme: editorTheme,
                    nodes: [LinkNode, HeadingNode, ParagraphNode, ListNode, ListItemNode],
                }}
            >
                <PrefillPlugin initialHtml={recruiterSignature?.signature ?? RTEInitValue} />
                <HistoryPlugin />
                <ToolbarPlugin features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}>
                    <PlaceholderPlugins placeholders={signaturePlaceholders} />
                </ToolbarPlugin>
                <OnChangePlugin ignoreHistoryMergeTagChange ignoreSelectionChange onChange={handleEditorOnChange} />
                <ListPlugin />
                <LinkPlugin />
                <RichTextPlugin
                    ErrorBoundary={LexicalErrorBoundary}
                    contentEditable={
                        <RichContentEditable
                            className={`max-h-32 min-h-32 rounded-b-xl p-3 text-sm [&_p]:text-sm [&_p]:leading-relaxed [&_p]:tracking-normal ${
                                signatureFieldErrorMessage ? 'border-destructive border' : ''
                            }`}
                            style={{ overflowY: 'auto' }}
                        />
                    }
                />
            </LexicalComposer>
            {signatureFieldErrorMessage ? (
                <FieldError className="mt-1">{String(signatureFieldErrorMessage)}</FieldError>
            ) : null}
        </>
    );
};

export default ProfileSignatureRTE;
